import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { API_URLS, BACKEND_URL, ROLE_LABELS, CREATABLE_ROLES_BY_ACTOR } from "../config";
import { apiRequest } from "../utils/apiRequest";
import AccessDenied from "../Components/AccessDenied";
import ChoiceSelect from "../Components/ChoiceSelect";
import PhoneField from "../Components/PhoneField";
import SessionList from "../Components/SessionList";
import {
  FiEdit, FiPlus, FiEye, FiTrash2,
  FiSearch, FiChevronUp, FiChevronDown, FiUser, FiRefreshCw,
  FiFileText, FiBriefcase, FiCheck, FiX, FiEyeOff, FiArrowLeft, FiMonitor
} from "react-icons/fi";
import { useToast } from "../Components/Toast";
import "./../css/Styles.css";

const RANK = {
  global_superadmin: 0, company_admin: 1, admin: 2,
  hod: 3, project_manager: 4, coordinator: 5, it: 5, finance: 1,
};

const EXCLUDED_MANAGER_ROLES = new Set(['global_superadmin', 'company_admin', 'admin']);

const EMPTY_FORM = {
  id: "",
  employeeType: "admin", reportingManager: [], company: "",
  fullName: "", address: "", mobile: "", email: "",
  dob: "", joiningDate: "", designation: "", password: "",
  status: "active", remark: ""
};

const STATUS_OPTIONS = [
  { value: "active", label: "ACTIVE" },
  { value: "inactive", label: "INACTIVE" },
];

const UserManagement = () => {
  const { token, user: currentUser, canAccessPage, canCallApi } = useAuth();
  const toast = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // ── Access flags — driven by the user's actual DB access doc ─────────────
  const canAddUser = canAccessPage("user_form");
  const canViewUser = canAccessPage("view_user");
  const canEditUser = canAccessPage("edit_user");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const result = await apiRequest(`${API_URLS.USERS}?limit=0`, {}, token);
    if (!result) return;
    if (result.denied) { setDenied(result); setLoading(false); return; }
    if (result.ok) {
      const rows = (result.data.rows || []).map((u) => ({
        id: u.id || u._id,
        name: u.employeeName,
        code: u.employeeCode,
        type: ROLE_LABELS[u.employeeType?.toLowerCase()] || u.employeeType,
        roleKey: u.employeeType,
        address: u.address,
        mobile: u.mobileNumber,
        email: u.emailId,
        status: u.status,
        company: u.company?.companyName || "Global",
        isCompanyAdmin: u.isCompanyAdmin,
        createdBy: u.createdBy,
      }));
      setUsers(rows);
    }
    setLoading(false);
  }, [token]); // useCallback — re-create only when token changes

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { setPage(1); }, [searchQuery, limit]);

  // ── Permission helpers for row-level actions ──────────────────────────────
  const canDeleteRow = (u) => {
    if (!currentUser) return false;
    if (u.id === (currentUser.id || currentUser._id)) return false;
    if (u.isCompanyAdmin) return currentUser.role === "global_superadmin";
    if ((RANK[currentUser.role] ?? 99) >= (RANK[u.roleKey] ?? 99)) return false;
    if (currentUser.role === "admin")
      return u.createdBy?.toString() === (currentUser.id || currentUser._id)?.toString();
    return true;
  };

  const canEditRow = (u) => {
    if (!currentUser || !canEditUser) return false;
    // Global super admins can edit (including change the password of) themselves and
    // other global super admins — the rank check below would otherwise always say no,
    // since global_superadmin can never outrank another global_superadmin.
    if (currentUser.role === "global_superadmin" && u.roleKey === "global_superadmin") return true;
    if (u.isCompanyAdmin && currentUser.role !== "global_superadmin") return false;
    return (RANK[currentUser.role] ?? 99) < (RANK[u.roleKey] ?? 99) ||
      currentUser.role === "company_admin";
  };

  // Mirrors the backend's canManage() (config-driven rank + company-admin guard) —
  // used to gate the "Sessions" (active-devices) action.
  const canManageSessionsRow = (u) => {
    if (!currentUser) return false;
    if (u.isCompanyAdmin) return currentUser.role === "global_superadmin";
    return (RANK[currentUser.role] ?? 99) < (RANK[u.roleKey] ?? 99);
  };
  const canManageSessions = currentUser.role === "global_superadmin" || canCallApi("users_sessions_view");

  // ── Delete / toggle handlers ──────────────────────────────────────────────
  const handleDelete = () => {
    if (selectedIds.length === 0) return;
    const u = users.find((x) => x.id === selectedIds[0]);
    if (!u) return;
    if (!canDeleteRow(u)) { toast.error("You don't have permission to delete this user."); return; }
    toast.deleteConfirm(`Delete ${u.name}? This cannot be undone.`, async () => {
      const result = await apiRequest(`${API_URLS.USERS}/${u.id}`, { method: "DELETE" }, token);
      if (!result) return;
      if (result.denied) { toast.error(result.message); return; }
      if (result.ok) {
        setUsers((prev) => prev.filter((x) => x.id !== u.id));
        setSelectedIds([]);
        toast.success("User deleted successfully!");
      } else {
        toast.error(result.data?.message || "Failed to delete user.");
      }
    });
  };

  const handleToggle = async () => {
    if (selectedIds.length === 0) return;
    const u = users.find((x) => x.id === selectedIds[0]);
    if (!u) return;
    const result = await apiRequest(`${API_URLS.USERS}/${u.id}/toggle-status`, { method: "PUT" }, token);
    if (!result) return;
    if (result.ok) {
      setUsers((prev) =>
        prev.map((x) => x.id === u.id ? { ...x, status: result.data.status } : x)
      );
    } else {
      toast.error(result.data?.message || "Failed to toggle status.");
    }
  };

  // ── Sessions (Active Devices) modal ───────────────────────────────────────
  const [sessionsUser, setSessionsUser] = useState(null); // { id, name } | null

  // ── Modal & Form State ────────────────────────────────────────────────────
  const [modal, setModal] = useState(null); // 'add' | 'edit' | 'view'
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({ main: true, basic: true, emp: true });

  const [managers, setManagers] = useState([]);
  const [companies, setCompanies] = useState([]);

  // ── Form Fetching ─────────────────────────────────────────────────────────
  const fetchFormOptions = useCallback(async () => {
    const isGlobalSA = currentUser?.role === "global_superadmin";

    try {
      const [mRes, cRes] = await Promise.all([
        apiRequest(`${API_URLS.USERS}/managers`, {}, token),
        isGlobalSA ? apiRequest(API_URLS.COMPANIES, {}, token) : Promise.resolve(null)
      ]);

      if (mRes?.ok) {
        // Backend might wrap in { data: [...] } or { rows: [...] }
        const mData = mRes.data.data || mRes.data.rows || (Array.isArray(mRes.data) ? mRes.data : []);
        setManagers(mData);
      }

      if (cRes?.ok) {
        const cData = cRes.data.data || cRes.data.rows || (Array.isArray(cRes.data) ? cRes.data : []);
        setCompanies(cData);
      }
    } catch (err) { console.error("Fetch options error:", err); }
  }, [token, currentUser?.role]);

  useEffect(() => {
    if (modal) fetchFormOptions();
  }, [modal, fetchFormOptions]);

  // ── Modal Handlers ────────────────────────────────────────────────────────
  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setFile(null);
    setProfilePreview(null);
    setModal("add");
  };

  const openEdit = async (userId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URLS.USERS}/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFormData({
          id: data.id || data._id || "",
          employeeType: data.employeeType || "",
          reportingManager: Array.isArray(data.reportingManager)
            ? data.reportingManager.map(m => m._id || m)
            : (data.reportingManager ? [data.reportingManager._id || data.reportingManager] : []),
          fullName: data.name || "",
          address: data.address || "",
          mobile: data.contactNumber || "",
          email: data.email || "",
          dob: data.dateOfBirth ? data.dateOfBirth.split("T")[0] : "",
          joiningDate: data.joiningDate ? data.joiningDate.split("T")[0] : "",
          designation: data.designation || "",
          password: "",
          remark: data.remark || "",
          status: data.status || "active",
          company: data.company || "",
        });
        setProfilePreview(data.profileUpload ? `${BACKEND_URL}${data.profileUpload}` : null);
        setFile(null);
        setModal("edit");
      }
    } catch (err) { console.error("Open edit error:", err); }
    finally { setLoading(false); }
  };

  const openView = async (userId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URLS.USERS}/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Normalize for view mode too
        setFormData({
          employeeType: data.employeeType || "",
          reportingManager: Array.isArray(data.reportingManager)
            ? data.reportingManager.map(m => m._id || m)
            : (data.reportingManager ? [data.reportingManager._id || data.reportingManager] : []),
          fullName: data.name || "",
          address: data.address || "",
          mobile: data.contactNumber || "",
          email: data.email || "",
          dob: data.dateOfBirth ? data.dateOfBirth.split("T")[0] : "",
          joiningDate: data.joiningDate ? data.joiningDate.split("T")[0] : "",
          designation: data.designation || "",
          company: data.company || "",
          status: data.status || "active",
          profileUpload: data.profileUpload
        });
        setProfilePreview(data.profileUpload ? `${BACKEND_URL}${data.profileUpload}` : null);
        setModal("view");
      }
    } catch (err) { console.error("Open view error:", err); }
    finally { setLoading(false); }
  };

  const getCompanyName = (comp) => {
    if (!comp) return "";
    const targetId = String(comp._id || comp);
    if (typeof comp === 'object' && comp.companyName) return comp.companyName;
    const opt = companyOptions.find(o => String(o.value) === targetId);
    return opt ? opt.label : (typeof comp === 'string' && comp.length < 20 ? comp : "");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const f = e.target.files[0];
      setFile(f);
      setProfilePreview(URL.createObjectURL(f));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = new FormData();
    const isEdit = modal === 'edit';

    // Map fields
    payload.append("role", formData.employeeType);
    payload.append("fullName", formData.fullName);
    payload.append("detailsAddress", formData.address);
    payload.append("mobileNumber", formData.mobile);
    payload.append("email", formData.email);
    payload.append("dateOfBirth", formData.dob);
    payload.append("joiningDate", formData.joiningDate);
    if (formData.password) payload.append(isEdit ? "newPassword" : "password", formData.password);
    if (formData.designation) payload.append("designation", formData.designation);
    if (formData.remark) payload.append("remark", formData.remark);
    if (formData.status) payload.append("status", formData.status.toLowerCase());
    if (formData.reportingManager?.length) {
      formData.reportingManager.forEach(id => payload.append("reportingManager", id?._id || id));
    }
    if (formData.company) payload.append("company", formData.company?._id || formData.company);
    if (file) payload.append("profileUpload", file);

    try {
      const url = isEdit ? `${API_URLS.USERS}/${selectedIds[0]}` : API_URLS.USERS;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: payload
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(isEdit ? "User updated!" : "User added!");
        setModal(null);
        fetchUsers();
        setSelectedIds([]);
      } else {
        toast.error(data.message || "Operation failed.");
      }
    } catch (err) { console.error("Submit error:", err); }
    finally { setSubmitting(false); }
  };

  // ── Options calculation ───────────────────────────────────────────────────
  const employeeTypeOptions = useMemo(() => {
    const roles = CREATABLE_ROLES_BY_ACTOR[currentUser?.role] || CREATABLE_ROLES_BY_ACTOR.global_superadmin || [];
    const finalRoles = modal === 'edit' && !roles.includes(formData.employeeType)
      ? [...roles, formData.employeeType].filter(Boolean)
      : roles;
    return finalRoles.map(r => ({ value: r, label: ROLE_LABELS[r] || r }));
  }, [currentUser?.role, modal, formData.employeeType]);

  const companyOptions = useMemo(() =>
    companies.map(c => ({ value: c._id || c.id, label: c.companyName })),
    [companies]);

  const managerOptions = useMemo(() => {
    const selfId = formData.id;
    const selectedRank = RANK[formData.employeeType] ?? 999;
    return managers
      .filter(m => {
        const mId = m._id || m.id;
        if (selfId && mId === selfId) return false;
        if (EXCLUDED_MANAGER_ROLES.has(m.role)) return false;
        return (RANK[m.role] ?? 999) < selectedRank;
      })
      .map(m => ({
        value: m._id || m.id,
        label: `${m.fullName} (${ROLE_LABELS[m.role] || m.role})`
      }));
  }, [managers, formData.employeeType, formData.id]);

  // ── Sorting / filtering ───────────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const vA = a[sortConfig.key]?.toString().toLowerCase() || "";
    const vB = b[sortConfig.key]?.toString().toLowerCase() || "";
    if (vA < vB) return sortConfig.direction === "asc" ? -1 : 1;
    if (vA > vB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredUsers = sortedUsers.filter((u) =>
    Object.values(u).some((v) =>
      v?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const pages = Math.max(1, Math.ceil(filteredUsers.length / limit));
  const pagedUsers = filteredUsers.slice((page - 1) * limit, page * limit);

  const toggleSelectAll = () =>
    setSelectedIds(selectedIds.length === pagedUsers.length ? [] : pagedUsers.map((u) => u.id));
  const toggleSelectRow = (id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const selectedCount = selectedIds.length;
  const lastSelected = users.find((u) => u.id === selectedIds[selectedIds.length - 1]);

  // ── Access denied ─────────────────────────────────────────────────────────
  if (denied) {
    return (
      <div className="vendor-page">
        <div className="management-card">
          <AccessDenied message={denied.message} required={denied.required} />
        </div>
      </div>
    );
  }

  return (
    <div className="vendor-page">

      {/* ── Sticky Action Bar ─────────────────────────────────────────────── */}
      <div className="vendor-action-bar">
        <div className="action-buttons">

          {/* New User — now opens modal */}
          {canAddUser && (
            <button className="new-user-btn" onClick={openAdd}>
              <FiPlus /> New User
            </button>
          )}

          {/* View — now opens modal */}
          {canViewUser && (
            <button
              className="action-btn view-btn"
              disabled={selectedCount !== 1}
              onClick={() => openView(selectedIds[0])}
            >
              <FiEye /> View
            </button>
          )}

          {/* Edit — now opens modal */}
          {canEditUser && (
            <button
              className="action-btn edit-btn"
              disabled={selectedCount !== 1 || !canEditRow(lastSelected || {})}
              onClick={() => openEdit(selectedIds[0])}
            >
              <FiEdit /> Edit
            </button>
          )}

          {/* Delete — global_superadmin always + others if can manage */}
          {(currentUser.role === 'global_superadmin' || canAddUser) && (
            <button
              className="action-btn delete-btn"
              disabled={selectedCount === 0 || !canDeleteRow(lastSelected || {})}
              onClick={handleDelete}
            >
              <FiTrash2 /> Delete
            </button>
          )}


          {/* Toggle Status — global_superadmin always + others if can manage */}
          {(currentUser.role === 'global_superadmin' || canAddUser) && (
            <button
              className="action-btn"
              style={{ borderColor: "#6b7280", color: "#6b7280" }}
              disabled={selectedCount === 0}
              onClick={handleToggle}
              title="Toggle active/inactive"
            >
              <FiRefreshCw size={13} /> Toggle Status
            </button>
          )}

          {/* Sessions — view/force-logout this user's active devices */}
          {canManageSessions && (
            <button
              className="action-btn"
              style={{ borderColor: "#6b7280", color: "#6b7280" }}
              disabled={selectedCount !== 1 || !canManageSessionsRow(lastSelected || {})}
              onClick={() => setSessionsUser({ id: lastSelected.id, name: lastSelected.name })}
              title="View active devices"
            >
              <FiMonitor size={13} /> Sessions
            </button>
          )}


        </div>

        <div className="search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search entries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table Card ────────────────────────────────────────────────────── */}
      <div className="management-card">
        <div className="management-header" onClick={() => setIsTableCollapsed(!isTableCollapsed)}>
          <div className="header-title">
            <FiUser size={18} />
            <h2>User Management</h2>
          </div>
          <div className="header-actions">
            {isTableCollapsed ? <FiChevronDown /> : <FiChevronUp />}
          </div>
        </div>

        {!isTableCollapsed && (
          <div className="vendor-table-container">
            {loading ? (
              <div style={{ padding: "2rem", textAlign: "center" }}>Loading users…</div>
            ) : (
              <table className="vendor-table">
                <thead>
                  <tr>
                    <th style={{ width: 16 }}>
                      <input
                        type="checkbox"
                        checked={selectedCount > 0 && selectedCount === pagedUsers.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    {[
                      { key: "id", label: "SR.NO" },
                      { key: "name", label: "EMPLOYEE NAME" },
                      { key: "code", label: "EMPLOYEE CODE" },
                      { key: "type", label: "EMPLOYEE TYPE" },
                      { key: "company", label: "COMPANY" },
                      { key: "address", label: "ADDRESS" },
                      { key: "mobile", label: "MOBILE NUMBER" },
                      { key: "email", label: "EMAIL ID" },
                      { key: "status", label: "STATUS" },
                    ].map(({ key, label }) => (
                      <th key={key} onClick={() => handleSort(key)}>
                        <div className="th-content">
                          {label}
                          <div className="sort-icons">
                            <FiChevronUp className={sortConfig.key === key && sortConfig.direction === "asc" ? "active" : ""} />
                            <FiChevronDown className={sortConfig.key === key && sortConfig.direction === "desc" ? "active" : ""} />
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
                        No users found
                      </td>
                    </tr>
                  ) : pagedUsers.map((user, index) => (
                    <tr
                      key={user.id || index}
                      className={selectedIds.includes(user.id) ? "selected" : ""}
                      onClick={() => toggleSelectRow(user.id)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(user.id)}
                          onChange={() => toggleSelectRow(user.id)}
                        />
                      </td>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>{user.name}</td>
                      <td>{user.code}</td>
                      <td>{user.type}</td>
                      <td>{user.company}</td>
                      <td>{user.address}</td>
                      <td>{user.mobile}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`status-badge ${user.status?.toLowerCase() === "active" ? "active" : "inactive"}`}>
                          {user.status?.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && filteredUsers.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, padding: '16px 20px' }}>
                <span style={{ fontSize: 12, color: '#888', marginRight: 'auto' }}>
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, filteredUsers.length)} of {filteredUsers.length}
                </span>
                <select
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <span style={{ fontSize: 12, color: '#888' }}>Page {page} of {pages}</span>
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => p + 1)}
                  style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: page >= pages ? 'not-allowed' : 'pointer', opacity: page >= pages ? 0.5 : 1 }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* ── Modal Overlay ─────────────────────────────────────────────────── */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.47)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", width: "85%", maxWidth: "1100px", maxHeight: "95vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)", borderRadius: "2px" }}>

            {/* Modal Header */}
            <div style={{ background: "#1a2a4d", color: "#fff", padding: "12px 30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 600, fontSize: 16 }}>
                {modal === "add" ? "Add New Employee" : modal === "edit" ? "Edit Employee" : `Employee Details - ${formData.name || ''}`}
              </span>
              <button onClick={() => setModal(null)} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center" }}>
                <FiX />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-content-wrapper" style={{ padding: "30px" }}>
              <form onSubmit={modal === 'view' ? (e) => e.preventDefault() : handleSubmit}>
                <div className="sub-card-container" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "25px" }}>

                  {/* Basic Info Card */}
                  <div className="sub-card">
                    <div className={`sub-card-header ${expanded.basic ? 'expanded' : ''}`} onClick={() => setExpanded(e => ({ ...e, basic: !e.basic }))}>
                      <h3><FiFileText size={16} /> Basic Information</h3>
                      {expanded.basic ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                    </div>
                    {expanded.basic && (
                      <div className="sub-card-content" style={{ padding: "20px" }}>
                        <div className="form-grid" style={{ display: "grid", gap: "15px" }}>
                          <div className="form-field">
                            <label>Employee Full Name <span className="required">*</span></label>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                              <input name="fullName" type="text" value={formData.fullName} onChange={handleFormChange} required disabled={modal === 'view'} placeholder="Full Name" style={{ flex: 1 }} />
                              {profilePreview && (
                                <img src={profilePreview} alt="preview" style={{ width: 38, height: 38, objectFit: "cover", borderRadius: 4, border: "1px solid #ddd" }} />
                              )}
                            </div>
                          </div>
                          <div className="form-field">
                            <label>Details Address <span className="required">*</span></label>
                            <input name="address" type="text" value={formData.address} onChange={handleFormChange} required disabled={modal === 'view'} placeholder="Street Address" />
                          </div>
                          <div className="form-field">
                            <PhoneField
                              label="Mobile Number"
                              value={formData.mobile}
                              onChange={(val) => setFormData(prev => ({ ...prev, mobile: val }))}
                              disabled={modal === 'view'}
                              required
                            />
                          </div>
                          <div className="form-field">
                            <label>Date of Birth <span className="required">*</span></label>
                            <input name="dob" type="date" value={formData.dob} onChange={handleFormChange} required disabled={modal === 'view'} />
                          </div>
                          <div className="form-field" style={{ display: modal === 'view' ? 'none' : 'block' }}>
                            <label>Profile Photo</label>
                            <div className="upload-controls" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <label htmlFor="user-profile" className="custom-file-upload" style={{ margin: 0 }}>Choose file</label>
                              <input type="file" id="user-profile" accept="image/*" onChange={handleFileChange} style={{ display: "none" }} />
                              <span style={{ fontSize: 11, color: "#777" }}>{file ? file.name : "No file chosen"}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Employee Info Card */}
                  <div className="sub-card">
                    <div className={`sub-card-header ${expanded.emp ? 'expanded' : ''}`} onClick={() => setExpanded(e => ({ ...e, emp: !e.emp }))}>
                      <h3><FiBriefcase size={16} /> Employee Information</h3>
                      {expanded.emp ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                    </div>
                    {expanded.emp && (
                      <div className="sub-card-content" style={{ padding: "20px" }}>
                        <div className="form-grid" style={{ display: "grid", gap: "15px" }}>
                          {/* Global super admins have no company — omit the field entirely
                              rather than showing/requiring one that doesn't apply to them. */}
                          {formData.employeeType === "global_superadmin" ? null : currentUser?.role === "global_superadmin" ? (
                            <div className="form-field">
                              <label>Company <span className="required">*</span></label>
                              <ChoiceSelect
                                name="company"
                                value={formData.company?._id || formData.company}
                                onChange={handleFormChange}
                                options={companyOptions}
                                placeholder="Select Company"
                                searchEnabled
                                required
                                disabled={modal === 'view'}
                              />
                            </div>
                          ) : (
                            <div className="form-field">
                              <label>Company</label>
                              <input
                                type="text"
                                value={getCompanyName(formData.company) || (modal === 'add' ? (currentUser?.company?.companyName || currentUser?.companyName) : "") || "Global"}
                                disabled
                                style={{ background: "#f9f9f9", color: "#666" }}
                              />
                            </div>
                          )}

                          <div className="form-field">
                            <label>Employee Type <span className="required">*</span></label>
                            <ChoiceSelect name="employeeType" value={formData.employeeType} onChange={handleFormChange} options={employeeTypeOptions} placeholder="Select Role" required disabled={modal === 'view'} />
                          </div>

                          <div className="form-field">
                            <label>Email ID <span className="required">*</span></label>
                            <input name="email" type="email" value={formData.email} onChange={handleFormChange} required disabled={modal === 'view'} placeholder="email@example.com" />
                          </div>

                          <div className="form-field">
                            <label>Joining Date <span className="required">*</span></label>
                            <input name="joiningDate" type="date" value={formData.joiningDate} onChange={handleFormChange} required disabled={modal === 'view'} />
                          </div>

                          {modal !== 'view' && (
                            <div className="form-field">
                              <label>{modal === "edit" ? "Update Password" : "Password"}</label>
                              <div className="input-wrapper" style={{ position: "relative" }}>
                                <input name="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={handleFormChange} placeholder={modal === "edit" ? "Leave blank to keep current" : "Enter Password"} required={modal === "add"} />
                                <span onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#666" }}>
                                  {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="form-field">
                            <label>Designation</label>
                            <input name="designation" type="text" value={formData.designation} onChange={handleFormChange} disabled={modal === 'view'} placeholder="Enter Designation" />
                          </div>

                          <div className="form-field">
                            <label>Reporting Manager</label>
                            <ChoiceSelect name="reportingManager" value={formData.reportingManager} onChange={handleFormChange} options={managerOptions} placeholder="Select managers..." searchEnabled multiple disabled={modal === 'view'} />
                          </div>

                          {(modal === "edit" || modal === 'view') && (
                            <div className="form-field">
                              <label>Status <span className="required">*</span></label>
                              <ChoiceSelect name="status" value={formData.status} onChange={handleFormChange} options={STATUS_OPTIONS} required disabled={modal === 'view'} />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="form-footer" style={{ marginTop: "30px", borderTop: "1px solid #ececec", paddingTop: "20px", display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                  {modal === 'view' ? (
                    <button type="button" className="action-btn primary" onClick={() => setModal(null)}>
                      <FiArrowLeft size={16} /> Back to List
                    </button>
                  ) : (
                    <>
                      <button type="submit" className="save-btn" disabled={submitting} style={{ background: "#3498db", color: "white" }}>
                        <FiCheck /> {submitting ? "Saving..." : modal === "edit" ? "Update User" : "Save User"}
                      </button>
                      <button type="button" className="cancel-btn" onClick={() => setModal(null)}>Cancel</button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Sessions (Active Devices) Modal ──────────────────────────────── */}
      {sessionsUser && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.47)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", width: "90%", maxWidth: "560px", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)", borderRadius: "2px" }}>
            <div style={{ background: "#1a2a4d", color: "#fff", padding: "12px 30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 600, fontSize: 16 }}>Active Devices — {sessionsUser.name}</span>
              <button onClick={() => setSessionsUser(null)} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center" }}>
                <FiX />
              </button>
            </div>
            <div style={{ padding: "20px 30px" }}>
              <SessionList
                listUrl={`${API_URLS.USERS}/${sessionsUser.id}/sessions`}
                revokeUrl={(id) => `${API_URLS.USERS}/${sessionsUser.id}/sessions/${id}`}
                bulkRevokeUrl={`${API_URLS.USERS}/${sessionsUser.id}/sessions`}
                emptyLabel="This user has no active devices."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
